import _ from 'underscore';
import parsePhoneNumber from 'libphonenumber-js';

// when running in proxy, the T&C is not available
// so we need to hide T&C and skip the T&C check if the user is in proxy
export function isSignupRunningProxy() {
  return window.location.port === '12000';
}

export const businessInfoScema = {
  name: 'Business Info schema',
  type: 'object',
  properties: {
    email: {
      required: true,
      type: 'string',
      maxLength: 100,
      pattern: /^([^\s@,;]+@[\w\-.]+\.[A-Za-z]+)$/,
    },
    phone: {
      required: true,
      type: 'string',
      maxLength: 30,
      pattern: /^[\d\-\s()extX.]*$/,
    },
  },
};

export const getEmailError = (i18n, setError) => ((errors) => {
  let error = '';
  if (errors && errors.required) {
    error = i18n.getString(_TL_('You have to enter your email address to continue.'));
  } else if (errors && errors.maxLength) {
    error = i18n.getString(_TL_('Email address exceeds the maximum allowed length.'));
  } else if (errors && errors.pattern) {
    error = i18n.getString(_TL_('Enter a valid email address.'));
  }
  setError(error);
  if (error) {
    return [error];
  }
  return [];
});

export const getPhoneNumberError = (i18n, setError) => ((errors) => {
  let error = '';
  if (errors && errors.required) {
    error = i18n.getString(_TL_('You have to enter your phone number to continue.'));
  } else if (errors && errors.maxLength) {
    error = i18n.getString(_TL_('Phone number exceeds the maximum allowed length.'));
  } else if (errors && errors.pattern) {
    error = i18n.getString(_TL_('Please enter a valid phone number. You may use 0-9, ext, spaces, and the . () - + characters.'));
  }
  setError(error);
  if (error) {
    return [error];
  }
  return [];
});

export const composePhone = (phoneNumber = '', countryCode = '', callingCode = '') => {
  if (_.isEmpty(phoneNumber)) {
    return {
      phoneNumber,
      countryCode,
    };
  }

  const defaultCountryCode = countryCode;

  try {
    const parsedPhoneNumber = parsePhoneNumber(
      phoneNumber,
      defaultCountryCode || undefined
    );

    return {
      // change to callingCode-nathonalNumber format, to be easier parse by account detail step
      phoneNumber: `${parsedPhoneNumber.countryCallingCode}-${parsedPhoneNumber.nationalNumber}`,
      countryCode: parsedPhoneNumber.country || defaultCountryCode,
    };
  } catch (e) {
    if (
      _.isEmpty(callingCode)
      || phoneNumber.indexOf(`${callingCode}`) === 0
      || phoneNumber.indexOf(`+${callingCode}`) === 0
    ) {
      return { phoneNumber, countryCode: defaultCountryCode };
    }

    return {
      phoneNumber: `${callingCode}-${phoneNumber}`,
      countryCode: defaultCountryCode,
    };
  }
};

export const parsePhone = (phone, countries, preferCountryCode = '') => {
  const preferCallingCode =
    preferCountryCode
      ? _.get(_.findWhere(countries, { Code: preferCountryCode }), 'CountryCallingCode')
      : '1';

  let parsedPhoneData = {
    callingCode: preferCallingCode,
    callingCountryCode: preferCountryCode,
    phoneNumber: '',
  };

  if (_.isEmpty(phone)) {
    return parsedPhoneData;
  }

  // if phone number match the format +<callingCode>-<phoneNumber> or <callingCode>-<phoneNumber>,
  // we can skip call parsePhoneNumber to parse the phone number
  if (/^(\d+)\s?-\s?(.*$)/.test(phone)) {
    const matches = phone.match(/^(\d+)\s?-\s?(.*$)/);
    parsedPhoneData = {
      callingCode: matches[1],
      phoneNumber: matches[2],
    };

    // different country may have same calling code, eg: US and Canada has the same calling code 1
    // we need to find the best matched country by preferCountryCode
    const matchedCountries =
      _.filter(countries, country => country.CountryCallingCode === parsedPhoneData.callingCode);
    if (!_.isEmpty(matchedCountries)) {
      const bestMatchedCountry =
        _.findWhere(matchedCountries, { Code: preferCountryCode }) || _.first(matchedCountries);
      parsedPhoneData.callingCountryCode = bestMatchedCountry.Code;
    }

    return parsedPhoneData;
  }

  let parsedPhoneNumber = null;
  const defaultCountryCode = preferCountryCode;
  try {
    parsedPhoneNumber = parsePhoneNumber(phone, defaultCountryCode || undefined);

    parsedPhoneData = {
      callingCode: parsedPhoneNumber.countryCallingCode,
      phoneNumber: parsedPhoneNumber.number.replace(`+${parsedPhoneNumber.countryCallingCode}`, ''),
      callingCountryCode: parsedPhoneNumber.country || defaultCountryCode,
    };
  } catch (e) {
    const matchedCountry = _.find(countries, country =>
      phone.indexOf(`+${country.CountryCallingCode}`) === 0 && preferCountryCode === country.Code);

    if (matchedCountry) {
      parsedPhoneData = {
        callingCode: matchedCountry.CountryCallingCode,
        phoneNumber: phone.indexOf(`+${matchedCountry.CountryCallingCode}-`) === 0 ?
          phone.replace(`+${matchedCountry.CountryCallingCode}-`, '') : phone.replace(`+${matchedCountry.CountryCallingCode}`, ''),
        callingCountryCode: preferCountryCode,
      };
    } else {
      // When the phone can't be parsed and no country prefix matches, fall back to a
      // supported country. callingCountryCode and callingCode are sourced from the SAME
      // country entry so they can never drift out of sync — returning a country without (or
      // with a mismatched) calling code can silently disable the signup "Next" button, since
      // isBusinessInfoValid requires a truthy callingCode even though the phone and the
      // country dropdown look valid.
      const fallbackCountry = (preferCountryCode && _.findWhere(countries, { Code: preferCountryCode }))
        || _.findWhere(countries, { Code: 'US' });
      parsedPhoneData = {
        callingCountryCode: _.get(fallbackCountry, 'Code', 'US'),
        callingCode: _.get(fallbackCountry, 'CountryCallingCode', '1'),
        phoneNumber: phone,
      };
    }
  }

  return parsedPhoneData;
};
